import { useState } from "react";
import { toast } from "sonner";
import { X, Save, ShieldCheck, Plus, Trash2, Loader2 } from "lucide-react";
import { chActualizarDocumento } from "@/lib/prevelis";

const PRIO = {
  es: ["Inmediata", "Alta", "Media", "Baja", "Pendiente de valoración técnica"],
  fr: ["Immédiate", "Élevée", "Moyenne", "Faible", "À évaluer techniquement"],
};
const inp = "w-full px-2 py-1.5 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-500";
const CAMPOS = [
  ["peligro", "Peligro", "Danger"],
  ["origen", "Origen / actividad", "Origine"],
  ["expuestos", "Puestos / personas expuestas", "Personnes exposées"],
  ["consecuencias", "Posibles consecuencias", "Conséquences"],
  ["medidas_existentes", "Medidas existentes", "Mesures existantes"],
  ["medidas_necesarias", "Medidas necesarias", "Mesures nécessaires"],
  ["pendiente_verificacion", "Información pendiente de verificación", "À vérifier"],
];

export default function SuizaDeterminacionEditor({ doc, onClose, onSaved }) {
  const fr = doc.idioma === "fr";
  const [titulo, setTitulo] = useState(doc.titulo || "");
  const [peligros, setPeligros] = useState((doc.peligros || []).map((p) => ({ ...p })));
  const [busy, setBusy] = useState("");

  const upd = (i, k, v) => setPeligros((s) => s.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addRow = () => setPeligros((s) => [...s, { peligro: "", origen: "", expuestos: "", consecuencias: "", medidas_existentes: "", medidas_necesarias: "", prioridad: PRIO[fr ? "fr" : "es"][2], base_normativa: "", pendiente_verificacion: "", danger_particulier: false, requiere_appreciation: false }]);
  const delRow = (i) => setPeligros((s) => s.filter((_, idx) => idx !== i));

  const save = async (estado) => {
    setBusy(estado);
    try {
      const res = await chActualizarDocumento(doc.id, { titulo, peligros, estado });
      toast.success(estado === "validado" ? (fr ? "Document validé" : "Documento validado") : (fr ? "Modifications enregistrées" : "Cambios guardados"));
      onSaved && onSaved(res);
      if (estado === "validado") onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (fr ? "Échec de l'enregistrement" : "No se pudo guardar"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-6xl my-6" onClick={(e) => e.stopPropagation()} data-testid="ch-det-editor">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg">{fr ? "Réviser la Détermination des dangers" : "Revisar la Determinación de peligros"}</h3>
            <p className="text-xs text-slate-500">{fr ? "Éditez les dangers, corrigez les priorités puis validez. Le Word/PDF est régénéré." : "Edita los peligros, corrige prioridades y valida. El Word/PDF se regenera."}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <label className="block mb-3">
          <span className="text-xs text-slate-500">{fr ? "Titre" : "Título"}</span>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-testid="ch-det-titulo" className={inp + " mt-1"} />
        </label>

        <div className="space-y-3 max-h-[55vh] overflow-auto pr-1">
          {peligros.map((p, i) => (
            <div key={i} data-testid={`ch-det-peligro-${i}`} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
                <button onClick={() => delRow(i)} data-testid={`ch-det-del-${i}`} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {CAMPOS.map(([k, es, frl]) => (
                  <label key={k} className="block">
                    <span className="text-[11px] text-slate-500">{fr ? frl : es}</span>
                    <textarea rows={2} value={p[k] || ""} onChange={(e) => upd(i, k, e.target.value)} data-testid={`ch-det-${k}-${i}`} className={inp + " mt-0.5"} />
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <label className="flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-500">{fr ? "Priorité" : "Prioridad"}</span>
                  <select value={p.prioridad || ""} onChange={(e) => upd(i, "prioridad", e.target.value)} data-testid={`ch-det-prio-${i}`} className="px-2 py-1 rounded-md border border-slate-300 text-sm">
                    <option value="">—</option>
                    {PRIO[fr ? "fr" : "es"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" checked={!!p.danger_particulier} onChange={(e) => upd(i, "danger_particulier", e.target.checked)} data-testid={`ch-det-dp-${i}`} />
                  {fr ? "Danger particulier" : "Peligro particular"}
                </label>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" checked={!!p.requiere_appreciation} onChange={(e) => upd(i, "requiere_appreciation", e.target.checked)} data-testid={`ch-det-ap-${i}`} />
                  {fr ? "Appréciation du risque" : "Requiere apreciación del riesgo"}
                </label>
              </div>
            </div>
          ))}
          <button onClick={addRow} data-testid="ch-det-add" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">
            <Plus className="w-4 h-4" /> {fr ? "Ajouter un danger" : "Añadir peligro"}
          </button>
        </div>

        <div className="flex justify-end gap-2 mt-4 border-t border-slate-100 pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">{fr ? "Fermer" : "Cerrar"}</button>
          <button onClick={() => save("borrador")} disabled={!!busy} data-testid="ch-det-guardar" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-blue-600 text-blue-700 font-medium hover:bg-blue-50 disabled:opacity-50">
            {busy === "borrador" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {fr ? "Enregistrer" : "Guardar cambios"}
          </button>
          <button onClick={() => save("validado")} disabled={!!busy} data-testid="ch-det-validar" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">
            {busy === "validado" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {fr ? "Valider" : "Validar documento"}
          </button>
        </div>
      </div>
    </div>
  );
}
